import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SynthesisGroup } from '../api/types';
import { useAuth } from '../contexts/AuthContext';
import { weekNumber } from '../utils/weekNumber';
import { mergeCellStyle, cellStyleToCss } from '../utils/cellStyle';
import { addDaysUTC, diffDaysUTC, parseISODate } from '../utils/ganttGeometry';
import {
  BAR_HEIGHT,
  COLUMN_WIDTH,
  COMPILED_COLUMNS,
  CompiledRow,
  DAY_ROW_HEIGHT,
  DAY_WIDTH,
  HEADER_HEIGHT,
  INDENT_PX,
  LABEL_WIDTH,
  MONTH_ROW_HEIGHT,
  NUM_COL_WIDTH,
  WEEK_ROW_HEIGHT,
  buildCompiledRows,
  compiledColumnText,
  computeCompiledRowLayout,
} from '../utils/compiledChartLayout';

/**
 * Diagramme de Gantt COMPILÉ des synthèses automatiques : un seul diagramme
 * fusionnant les tâches de tous les diagrammes sources d'un dossier (voir
 * demande utilisateur), séparé par des bandeaux portant le nom du projet.
 * Volontairement un composant à part de GanttChart.tsx, pas un mode
 * supplémentaire de celui-ci : ni connecteurs ni chemin critique n'ont de
 * sens ici (les prédécesseurs ne sont même pas affichés — un numéro de
 * tâche peut se répéter d'un projet à l'autre), et l'interactivité
 * (glisser-lier, annuler) n'existe pas dans une vue en lecture seule
 * multi-diagrammes. Colonnes limitées à Nom(FR)/Nom(EN)/Durée/Début/Fin —
 * voir ARCHITECTURE.md. Constantes/helpers partagés avec l'export PDF du
 * même diagramme compilé — voir utils/compiledChartLayout.ts.
 */

export function CompiledSynthesisChart({ groups }: { groups: SynthesisGroup[] }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const dateFormat = user?.dateFormat ?? 'DD-MM-YYYY';
  const weekNumbering = user?.weekNumbering ?? 'ISO';

  const rows = useMemo(() => buildCompiledRows(groups), [groups]);

  const datedTasks = useMemo(
    () => rows.filter((r): r is CompiledRow & { kind: 'task' } => r.kind === 'task' && !!r.task.startDate && !!r.task.endDate).map((r) => r.task),
    [rows],
  );

  const rangeStart = useMemo(() => {
    if (datedTasks.length === 0) {
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      return addDaysUTC(today, -2);
    }
    let min = parseISODate(datedTasks[0].startDate!);
    for (const task of datedTasks) {
      const s = parseISODate(task.startDate!);
      if (s < min) min = s;
    }
    return addDaysUTC(min, -2);
  }, [datedTasks]);

  const totalDays = useMemo(() => {
    if (datedTasks.length === 0) return 30;
    let max = parseISODate(datedTasks[0].endDate!);
    for (const task of datedTasks) {
      const e = parseISODate(task.endDate!);
      if (e > max) max = e;
    }
    return Math.max(diffDaysUTC(rangeStart, max) + 4, 14);
  }, [datedTasks, rangeStart]);

  const xOfDate = (d: Date) => diffDaysUTC(rangeStart, d) * DAY_WIDTH;

  const dayHeaders = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < totalDays; i++) days.push(addDaysUTC(rangeStart, i));
    return days;
  }, [rangeStart, totalDays]);

  interface HeaderSpan {
    key: string;
    label: string;
    left: number;
    width: number;
  }

  const monthSpans = useMemo<HeaderSpan[]>(() => {
    const spans: HeaderSpan[] = [];
    let startIdx = 0;
    let currentKey = '';
    dayHeaders.forEach((d, i) => {
      const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      if (key !== currentKey) {
        if (currentKey) {
          spans.push({
            key: currentKey,
            label: dayHeaders[startIdx].toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
            left: startIdx * DAY_WIDTH,
            width: (i - startIdx) * DAY_WIDTH,
          });
        }
        currentKey = key;
        startIdx = i;
      }
    });
    if (currentKey) {
      spans.push({
        key: currentKey,
        label: dayHeaders[startIdx].toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
        left: startIdx * DAY_WIDTH,
        width: (dayHeaders.length - startIdx) * DAY_WIDTH,
      });
    }
    return spans;
  }, [dayHeaders, i18n.language]);

  const weekSpans = useMemo<HeaderSpan[]>(() => {
    const spans: HeaderSpan[] = [];
    let startIdx = 0;
    let currentWeek: number | null = null;
    const weekLabel = (n: number) => t('chart.weekAbbrev', { n });
    dayHeaders.forEach((d, i) => {
      const wk = weekNumber(d, weekNumbering);
      if (wk !== currentWeek) {
        if (currentWeek !== null) {
          spans.push({ key: `${startIdx}`, label: weekLabel(currentWeek), left: startIdx * DAY_WIDTH, width: (i - startIdx) * DAY_WIDTH });
        }
        currentWeek = wk;
        startIdx = i;
      }
    });
    if (currentWeek !== null) {
      spans.push({
        key: `${startIdx}`,
        label: weekLabel(currentWeek),
        left: startIdx * DAY_WIDTH,
        width: (dayHeaders.length - startIdx) * DAY_WIDTH,
      });
    }
    return spans;
  }, [dayHeaders, weekNumbering, t]);

  const rowLayout = useMemo(() => computeCompiledRowLayout(rows), [rows]);
  const totalHeight = rowLayout.length ? rowLayout[rowLayout.length - 1].top + rowLayout[rowLayout.length - 1].height : 0;

  const todayX = useMemo(() => {
    const d = new Date();
    return xOfDate(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart]);
  const showTodayLine = todayX >= 0 && todayX <= totalDays * DAY_WIDTH;

  return (
    <div className="card" style={{ display: 'flex', padding: 0, overflow: 'hidden' }}>
      <div style={{ width: LABEL_WIDTH, flexShrink: 0, borderRight: '1px solid var(--color-border)' }}>
        <div
          style={{
            height: HEADER_HEIGHT,
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'flex-end',
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          <div style={{ width: NUM_COL_WIDTH, flexShrink: 0 }} />
          {COMPILED_COLUMNS.map((col) => (
            <div key={col} style={{ width: COLUMN_WIDTH[col], flexShrink: 0, paddingBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t(`tasks.colShort.${col}`)}
            </div>
          ))}
        </div>
        {rowLayout.map(({ row, height }) => {
          if (row.kind === 'separator') {
            return (
              <div
                key={row.key}
                className="synthesis-group-row"
                style={{ height, display: 'flex', alignItems: 'center', paddingLeft: 8 }}
              >
                <Link to={`/diagrams/${row.diagramId}`}>{row.diagramName}</Link>
                {row.assigneeName && <span className="synthesis-assignee">{row.assigneeName}</span>}
              </div>
            );
          }
          const task = row.task;
          return (
            <div key={row.key} style={{ height, display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
              <div style={{ width: NUM_COL_WIDTH, flexShrink: 0, textAlign: 'right', paddingRight: 4, color: 'var(--color-text-muted)' }}>
                {task.number}
              </div>
              {COMPILED_COLUMNS.map((col, i) => {
                const css = cellStyleToCss(mergeCellStyle(task.formatting, col));
                return (
                  <div
                    key={col}
                    style={{
                      width: COLUMN_WIDTH[col],
                      flexShrink: 0,
                      paddingLeft: i === 0 ? (task.depth ?? 0) * INDENT_PX : 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      ...css,
                    }}
                  >
                    {i === 0 && task.isGroup && <span>📁</span>}
                    {compiledColumnText(task, col, dateFormat)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div style={{ overflowX: 'auto', flex: 1 }}>
        <div style={{ position: 'relative', width: totalDays * DAY_WIDTH }}>
          <div style={{ height: HEADER_HEIGHT, position: 'relative', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ position: 'relative', height: MONTH_ROW_HEIGHT, borderBottom: '1px solid var(--color-border)' }}>
              {monthSpans.map((span) => (
                <div
                  key={span.key}
                  style={{
                    position: 'absolute',
                    left: span.left,
                    top: 0,
                    width: span.width,
                    height: MONTH_ROW_HEIGHT,
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    borderLeft: '1px solid var(--color-border)',
                    textTransform: 'capitalize',
                  }}
                >
                  {span.label}
                </div>
              ))}
            </div>
            <div style={{ position: 'relative', height: WEEK_ROW_HEIGHT, borderBottom: '1px solid var(--color-border)' }}>
              {weekSpans.map((span) => (
                <div
                  key={span.key}
                  title={t('chart.weekTitle') as string}
                  style={{
                    position: 'absolute',
                    left: span.left,
                    top: 0,
                    width: span.width,
                    height: WEEK_ROW_HEIGHT,
                    textAlign: 'center',
                    fontSize: 10,
                    color: 'var(--color-text-muted)',
                    overflow: 'hidden',
                    borderLeft: '1px solid var(--color-border)',
                  }}
                >
                  {span.label}
                </div>
              ))}
            </div>
            <div style={{ position: 'relative', height: DAY_ROW_HEIGHT }}>
              {dayHeaders.map((d, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: i * DAY_WIDTH,
                    top: 0,
                    width: DAY_WIDTH,
                    height: DAY_ROW_HEIGHT,
                    textAlign: 'center',
                    fontSize: 10,
                    color: 'var(--color-text-muted)',
                    borderLeft: d.getUTCDay() === 1 ? '1px solid var(--color-border)' : undefined,
                    paddingTop: 2,
                  }}
                >
                  {d.getUTCDate()}
                </div>
              ))}
            </div>
          </div>

          <div style={{ position: 'relative', height: totalHeight }}>
            {rowLayout.map(({ row, top, height }) =>
              row.kind === 'separator' ? (
                <div
                  key={row.key}
                  className="synthesis-group-row"
                  style={{ position: 'absolute', left: 0, right: 0, top, height }}
                />
              ) : (
                <div
                  key={row.key}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top,
                    height,
                    borderBottom: '1px solid var(--color-border)',
                  }}
                />
              ),
            )}

            {rowLayout.map(({ row, top, height }) => {
              if (row.kind !== 'task') return null;
              const task = row.task;
              if (!task.startDate || !task.endDate) return null;
              const start = parseISODate(task.startDate);
              const end = parseISODate(task.endDate);
              const isMilestone = task.startDate.slice(0, 10) === task.endDate.slice(0, 10);
              const barLeft = xOfDate(start);
              const barWidth = (diffDaysUTC(start, end) + 1) * DAY_WIDTH;
              const y = top + height / 2;
              const color = task.isGroup ? '#475569' : 'var(--color-primary)';

              if (isMilestone) {
                const cx = barLeft + DAY_WIDTH / 2;
                return (
                  <div
                    key={row.key}
                    title={task.nameFr}
                    style={{
                      position: 'absolute',
                      left: cx - 7,
                      top: y - 7,
                      width: 14,
                      height: 14,
                      boxSizing: 'border-box',
                      transform: 'rotate(45deg)',
                      background: color,
                      borderRadius: 2,
                    }}
                  />
                );
              }
              return (
                <div
                  key={row.key}
                  title={`${task.nameFr} (${task.progressPct}%)`}
                  style={{
                    position: 'absolute',
                    left: barLeft,
                    top: y - BAR_HEIGHT / 2,
                    width: Math.max(barWidth, 4),
                    height: BAR_HEIGHT,
                    boxSizing: 'border-box',
                    borderRadius: 3,
                    background: color,
                    opacity: task.isGroup ? 0.9 : 1,
                  }}
                >
                  {!task.isGroup && task.progressPct > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${Math.min(task.progressPct, 100)}%`,
                        background: 'rgba(0,0,0,0.25)',
                        borderRadius: '3px 0 0 3px',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {showTodayLine && (
            <div
              title={t('chart.todayLine') as string}
              style={{
                position: 'absolute',
                left: todayX,
                top: 0,
                width: 1,
                height: HEADER_HEIGHT + totalHeight,
                background: 'var(--color-danger)',
                pointerEvents: 'none',
                zIndex: 5,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
